import { useState } from "react"

import { replaceLocation } from "@/helpers/window.helpers"
import { useLogger } from "@/hooks/useLogger"
import useServices from "@/hooks/useServices"
import { ErrorResult } from "@/organisms/ErrorBoundary"
import {
  handKodemSsoFailureToOpener,
  handKodemSsoSignInToOpener,
  isKodemSsoPopup
} from "@/pages/SignInPage/applications/authenticate"
import { Loader } from "@/pages/SignInPage/components/molecules/Loader/Loader"
import { SsoPopupSignedIn } from "@/pages/SignInPage/components/molecules/SsoPopupSignedIn"
import { SsoPopupSigningIn } from "@/pages/SignInPage/components/molecules/SsoPopupSigningIn"
import { useEffect } from "react"
import { useParams, useSearchParams } from "react-router-dom"

export const AcsAuth = () => {
  const { slug } = useParams()
  const [searchParams] = useSearchParams()
  const [isError, setError] = useState(false)
  // A Kodem SSO popup that could not close itself
  const [isPopupSignedIn, setPopupSignedIn] = useState(false)
  // The Kodem ACS puts the handoff token in the fragment, which the browser sends to no server
  const [ssoToken] = useState(() =>
    new URLSearchParams(window.location.hash.slice(1)).get("ssoToken")
  )
  const { auth } = useServices()
  const { logger, EVENTS } = useLogger()

  const isModal = window.opener && window.opener !== window

  const onClose = (replaceUrl: string) => {
    try {
      if (isModal) {
        window.opener.location.replace(replaceUrl)
        window.close()
      } else window.location.replace(replaceUrl)
    } catch (error: any) {
      logger.error(error, {
        tags: {
          custom_error: EVENTS.ERROR_EVENTS.SIGNIN_POPUP,
          provider: "saml",
          action: "close"
        }
      })
    }
  }

  useEffect(() => {
    const onError = async (
      tags: Record<string, boolean | string>,
      err: unknown = "SAML ACS Auth Error"
    ) => {
      logger.error(err instanceof Error ? err : new Error(String(err)), {
        tags: {
          ...tags,
          custom_error: EVENTS.ERROR_EVENTS.SIGN_IN,
          provider: "saml"
        }
      })
      // A Kodem SSO popup leaves the error to the sign-in page that opened it
      if (await handKodemSsoFailureToOpener()) return window.close()

      setError(true)
    }

    const fn = async () => {
      try {
        const token = searchParams.get("token")
        const firebaseTenantId = searchParams.get("firebaseTenantId")
        // Dropped from the address right away, so the handoff token stays out of the history
        if (ssoToken)
          window.history.replaceState(
            window.history.state,
            "",
            window.location.pathname + window.location.search
          )
        const isPopupError = !!searchParams.get("error")

        // Kodem SSO login: exchange the handoff token for the API session. The popup has no
        // window.opener (severed by the IdP's COOP), the sign-in page is told over a channel
        if (slug && ssoToken && !isPopupError) {
          await auth.signInWithSsoToken(slug, ssoToken)

          const handed = await handKodemSsoSignInToOpener()

          // No sign-in page opened this window (e.g. an IdP-started login): the app starts here
          if (!handed && !isKodemSsoPopup()) return replaceLocation("/")

          // A popup leaves the app to the sign-in pages, which share the session (one that missed
          // the outcome, e.g. while another tab's popup handed it over, signs in on reload)
          window.close()
          // Still open when the browser refuses a script to close the window
          return setPopupSignedIn(true)
        }
        const isParamsValid = slug && token && firebaseTenantId

        if (!isParamsValid || isPopupError)
          return onError({
            isPopupError: searchParams.get("error") ?? isPopupError,
            isParamsValid: !!isParamsValid,
            firebaseTenantId: !!firebaseTenantId,
            slug: !!slug,
            token: !!token
          })

        auth.setCompany(slug)
        auth.setTenantId(firebaseTenantId)
        await auth.signInWithToken(token)

        if (isModal) onClose(window.location.href)
      } catch (err) {
        onError({ catchSection: true }, err)
      }
    }
    fn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, slug, ssoToken])

  if (isError)
    return (
      <ErrorResult
        placement={isModal ? "SIGNIN_POPUP" : "LAYOUT"}
        errorType={isModal ? "ACL_SIGNIN" : "Unknown"}
        onClose={() => onClose("/")}
        onRefresh={() => onClose("/")}
      />
    )

  if (isPopupSignedIn) return <SsoPopupSignedIn />

  return isKodemSsoPopup() ? <SsoPopupSigningIn /> : <Loader />
}
